import { z } from "zod";
import dayjs from "dayjs";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { sanitizeText } from "@/lib/validators";
import {
  SUPPORTED_CURRENCIES,
  CURRENCY_SYMBOLS,
  CURRENCY_TO_LABEL,
  CURRENCY_GROUPS,
  CURRENCY_COMBOBOX_GROUPS,
  type SupportedCurrencies,
  type CurrencySymbols,
  type CurrencyComboboxItem,
  type CurrencyComboboxGroup,
} from "./currencies";

/**
 * Helper to create a sanitized string schema
 * Sanitization happens BEFORE validation to prevent XSS
 * Non-string values (except undefined/null) are converted to empty string
 */
function sanitizedString<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((val) => {
    if (typeof val === "string") {
      return sanitizeText(val);
    }
    // Convert non-string values (objects, arrays, etc.) to empty string
    // This handles corrupted/migrated localStorage data gracefully
    if (val !== undefined && val !== null) {
      return "";
    }
    return val;
  }, schema);
}

// Field configuration for invoice item payment fields
export interface InvoiceItemFieldConfig {
  field:
    | "paymentMethod"
    | "paymentDate"
    | "paymentPhoneNumber"
    | "paymentBankName"
    | "paymentBankNumber"
    | "paymentBranch"
    | "paymentAccount"
    | "paymentCardCompany"
    | "paymentCardType"
    | "paymentTransactionNumber"
    | "paymentNumberOfPayments"
    | "paymentCardLastFour"
    | "paymentCheckNumber"
    | "paymentCheckBankName"
    | "paymentCheckBranch"
    | "paymentCheckAccount"
    | "paymentCheckMaturityDate";
  label: string;
  tooltip: string;
  helper: string;
  placeholder: string;
  /**
   * Array of payment method values that should show this field.
   * If undefined/null, the field is always shown when its visibility checkbox is checked.
   * If empty array, the field is never shown based on payment method.
   * For Bit/PayBox: use "Bit / PayBox"
   * For Bank transfer: use "העברה בנקאית"
   * For Credit Card: use "כרטיס אשראי"
   * For Check: use "צ'ק"
   */
  showWhenPaymentMethod?: string[];
}

// Predefined field configurations for payment fields
export const INVOICE_ITEM_PAYMENT_FIELDS: InvoiceItemFieldConfig[] = [
  {
    field: "paymentDate",
    label: "תאריך תשלום",
    tooltip: "הצג את עמודת 'תאריך תשלום' ב-PDF",
    helper: "תאריך התשלום הספציפי לפריט זה",
    placeholder: "DD/MM/YYYY",
  },
  {
    field: "paymentMethod",
    label: "אמצעי תשלום",
    tooltip: "הצג את עמודת 'אמצעי תשלום' ב-PDF",
    helper:
      "ציין את אמצעי התשלום (העברה בנקאית, Bit, PayBox, מזומן, כרטיס אשראי)",
    placeholder: "למשל: העברה בנקאית, Bit, PayBox, מזומן",
  },
  {
    field: "paymentPhoneNumber",
    label: "מספר טלפון לתשלום",
    tooltip: "הצג את עמודת 'מספר טלפון' ב-PDF",
    helper: "מספר הטלפון המשויך לחשבון Bit/PayBox",
    placeholder: "למשל: 05X-XXX-XXXX",
    showWhenPaymentMethod: ["Bit / PayBox"],
  },
  // Credit Card fields - only show when "כרטיס אשראי" is selected
  {
    field: "paymentCardCompany",
    label: "חברת כרטיסי אשראי",
    tooltip: "הצג את עמודת 'חברת אשראי' ב-PDF",
    helper: "שם חברת כרטיסי האשראי (כגון: כאל, ישראכרט, לאומי קארד)",
    placeholder: "למשל: כאל, ישראכרט",
    showWhenPaymentMethod: ["כרטיס אשראי"],
  },
  {
    field: "paymentCardType",
    label: "סוג כרטיס",
    tooltip: "הצג את עמודת 'סוג כרטיס' ב-PDF",
    helper: "סוג הכרטיס (ויזה, מאסטרקארד, אמריקן אקספרס)",
    placeholder: "למשל: Visa, MasterCard",
    showWhenPaymentMethod: ["כרטיס אשראי"],
  },
  {
    field: "paymentTransactionNumber",
    label: "מספר אישור עסקה",
    tooltip: "הצג את עמודת 'מספר אישור' ב-PDF",
    helper: "מספר האישור שהתקבל ממערכת הסליקה",
    placeholder: "למשל: 12345678",
    showWhenPaymentMethod: ["כרטיס אשראי"],
  },
  {
    field: "paymentNumberOfPayments",
    label: "מספר תשלומים",
    tooltip: "הצג את עמודת 'מספר תשלומים' ב-PDF",
    helper: "מספר התשלומים (1 לתשלום רגיל, 3-36 לתשלומים)",
    placeholder: "למשל: 1, 3, 12",
    showWhenPaymentMethod: ["כרטיס אשראי"],
  },
  {
    field: "paymentCardLastFour",
    label: "4 ספרות אחרונות של הכרטיס",
    tooltip: "הצג את עמודת '4 ספרות אחרונות' ב-PDF",
    helper: "רק 4 הספרות האחרונות של הכרטיס לזיהוי (לפי תקן PCI)",
    placeholder: "למשל: 1234",
    showWhenPaymentMethod: ["כרטיס אשראי"],
  },
  // Check fields - only show when "צ'ק" is selected
  {
    field: "paymentCheckNumber",
    label: "מספר צ'ק",
    tooltip: "הצג את עמודת 'מספר צ\\'ק' ב-PDF",
    helper: "מספר הצ'ק כפי שרשום עליו",
    placeholder: "למשל: 123456",
    showWhenPaymentMethod: ["צ'ק"],
  },
  {
    field: "paymentCheckBankName",
    label: "שם הבנק (על הצ'ק)",
    tooltip: "הצג את עמודת 'שם הבנק' ב-PDF",
    helper: "שם הבנק שעליו מושך הצ'ק (למשל: לאומי, הפועלים)",
    placeholder: "למשל: בנק לאומי, בנק הפועלים",
    showWhenPaymentMethod: ["צ'ק"],
  },
  {
    field: "paymentCheckBranch",
    label: "מספר סניף (על הצ'ק)",
    tooltip: "הצג את עמודת 'מספר סניף' ב-PDF",
    helper: "מספר הסניף שעליו מושך הצ'ק",
    placeholder: "למשל: 123, 456",
    showWhenPaymentMethod: ["צ'ק"],
  },
  {
    field: "paymentCheckAccount",
    label: "מספר חשבון (על הצ'ק)",
    tooltip: "הצג את עמודת 'מספר חשבון' ב-PDF",
    helper: "מספר החשבון הבנקאי ממנו מושך הצ'ק",
    placeholder: "מספר החשבון הבנקאי",
    showWhenPaymentMethod: ["צ'ק"],
  },
  {
    field: "paymentCheckMaturityDate",
    label: "תאריך פרעון הצ'ק",
    tooltip: "הצג את עמודת 'תאריך פרעון' ב-PDF",
    helper: "תאריך הפרעון הרשום על הצ'ק (גם אם זה צ'ק דחוי)",
    placeholder: "DD/MM/YYYY",
    showWhenPaymentMethod: ["צ'ק"],
  },
  // Bank Transfer fields
  {
    field: "paymentBankName",
    label: "שם בנק",
    tooltip: "הצג את עמודת 'שם בנק' ב-PDF",
    helper: "שם הבנק שבו בוצע התשלום",
    placeholder: "למשל: בנק לאומי, בנק הפועלים",
    showWhenPaymentMethod: ["העברה בנקאית"],
  },
  {
    field: "paymentBankNumber",
    label: "מספר בנק",
    tooltip: "הצג את עמודת 'מספר בנק' ב-PDF",
    helper: "מספר הבנק. לרוב יתמלא אוטומטית בעת הזנת שם הבנק",
    placeholder: "למשל: 10, 12, 13",
    showWhenPaymentMethod: ["העברה בנקאית"],
  },
  {
    field: "paymentBranch",
    label: "מספר סניף",
    tooltip: "הצג את עמודת 'מספר סניף' ב-PDF",
    helper: "מספר הסניף בו מנוהל החשבון",
    placeholder: "למשל: 123, 456",
    showWhenPaymentMethod: ["העברה בנקאית"],
  },
  {
    field: "paymentAccount",
    label: "מספר חשבון",
    tooltip: "הצג את עמודת 'מספר חשבון' ב-PDF",
    helper: "מספר החשבון לתשלום",
    placeholder: "מספר החשבון הבנקאי",
    showWhenPaymentMethod: ["העברה בנקאית"],
  },
];

// Re-export currency types and data
export {
  SUPPORTED_CURRENCIES,
  CURRENCY_SYMBOLS,
  CURRENCY_TO_LABEL,
  CURRENCY_GROUPS,
  CURRENCY_COMBOBOX_GROUPS,
  type SupportedCurrencies,
  type CurrencySymbols,
  type CurrencyComboboxItem,
  type CurrencyComboboxGroup,
};

// Israeli Business Types
export const BUSINESS_TYPES = [
  "osek-patur",
  "eseq-zair",
  "osek-murshe",
] as const;

export type BusinessType = (typeof BUSINESS_TYPES)[number];

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  "osek-patur": "עוסק פטור",
  "eseq-zair": "עסק זעיר",
  "osek-murshe": "עוסק מורשה",
};

// Validates Israeli ID number (Teudat Zehut, company, etc.) using Luhn-variant algorithm
export function validateIsraeliId(idNumber: string): boolean {
  if (!idNumber) return false;
  const idStr = idNumber.replace(/[-\s]/g, "").padStart(9, "0");

  if (idStr.length !== 9 || !/^\d+$/.test(idStr)) {
    return false;
  }

  let total = 0;
  for (let i = 0; i < 9; i++) {
    const weight = i % 2 === 0 ? 1 : 2;
    let val = parseInt(idStr[i], 10) * weight;
    if (val > 9) {
      val = Math.floor(val / 10) + (val % 10);
    }
    total += val;
  }

  return total % 10 === 0;
}

// Validates sequential receipt numbering (Israeli requirement)
export function validateSequentialReceiptNumber(
  receiptNumber: string,
  lastReceiptNumber?: string,
): { isValid: boolean; message?: string } {
  if (!receiptNumber || receiptNumber.trim() === "") {
    return { isValid: false, message: "מספר קבלה הוא שדה חובה" };
  }

  const numericMatch = receiptNumber.match(/^(\D*)(\d+)$/);
  if (!numericMatch) {
    return { isValid: true };
  }

  const [, prefix, numStr] = numericMatch;
  const num = parseInt(numStr, 10);

  if (num <= 0) {
    return { isValid: false, message: "מספר קבלה חייב להיות חיובי" };
  }

  if (lastReceiptNumber) {
    const lastMatch = lastReceiptNumber.match(/^(\D*)(\d+)$/);
    if (lastMatch) {
      const [, lastPrefix, lastNumStr] = lastMatch;
      const lastNum = parseInt(lastNumStr, 10);
      if (prefix === lastPrefix && num <= lastNum) {
        return {
          isValid: false,
          message: `מספר הקבלה חייב להיות גדול מ-${lastReceiptNumber} (מספור רציף)`,
        };
      }
    }
  }

  return { isValid: true };
}

// Date formats - Israeli format as default
export const SUPPORTED_DATE_FORMATS = [
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "D MMMM YYYY",
  "MMMM D, YYYY",
  "DD.MM.YYYY",
  "DD-MM-YYYY",
  "YYYY.MM.DD",
] as const;

export const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

export const APP_VERSION = "1.0.0";
export const SCHEMA_VERSION = "1.0.0";

// Helper for field visibility toggles
const visible = (defaultValue = true) => z.boolean().default(defaultValue);

// Invoice Item Schema - only fields used in PDF
export const invoiceItemSchema = z
  .object({
    name: sanitizedString(
      z.string().max(500, "שם הפריט לא יכול להיות ארוך מ-500 תווים").trim(),
    ).optional(),
    nameFieldIsVisible: visible(),
    amount: z
      .union([z.string(), z.number()])
      .transform((val) => {
        // Allow empty string for initial form state
        if (typeof val === "string") {
          const trimmed = val.trim();
          if (trimmed === "") return "";
          return Number(trimmed);
        }
        return val;
      })
      .refine(
        (val) => {
          // Skip validation for empty string (initial state)
          if (val === "") return true;
          return !isNaN(val) && isFinite(val);
        },
        {
          message: "כמות חייבת להיות מספר תקין",
        },
      )
      .refine(
        (val) => {
          if (val === "") return true;
          return val > 0;
        },
        { message: "הכמות חייבת להיות חיובית" },
      )
      .refine(
        (val) => {
          if (val === "") return true;
          return val <= 9_999_999_999.99;
        },
        { message: "הכמות לא יכולה לעבור 9,999,999,999.99" },
      ),
    netPrice: z
      .union([z.string(), z.number()])
      .transform((val) => {
        // Allow empty string for initial form state
        if (typeof val === "string") {
          const trimmed = val.trim();
          if (trimmed === "") return "";
          return Number(trimmed);
        }
        return val;
      })
      .refine(
        (val) => {
          // Skip validation for empty string (initial state)
          if (val === "") return true;
          return !isNaN(val) && isFinite(val);
        },
        {
          message: "סכום נטו חייב להיות מספר תקין",
        },
      )
      .refine(
        (val) => {
          if (val === "") return true;
          return val >= 0;
        },
        { message: "סכום נטו חייב להיות 0 או יותר" },
      )
      .refine(
        (val) => {
          if (val === "") return true;
          return val <= 100_000_000_000;
        },
        { message: "סכום נטו לא יכול לעבור 100 מיליארד" },
      ),
    preTaxAmount: z
      .union([z.string(), z.number()])
      .transform((val) => {
        // Allow empty string for initial form state
        if (typeof val === "string") {
          const trimmed = val.trim();
          if (trimmed === "") return "";
          const num = Number(trimmed);
          if (isNaN(num)) throw new Error("סכום חייב להיות מספר");
          return num;
        }
        return val;
      })
      .refine(
        (val) => {
          // Skip validation for empty string (initial state)
          if (val === "") return true;
          return !isNaN(val) && isFinite(val);
        },
        {
          message: "סכום לפני מס חייב להיות מספר תקין",
        },
      )
      .refine(
        (val) => {
          if (val === "") return true;
          return val >= 0;
        },
        { message: "סכום לפני מס חייב להיות לא-שלילי" },
      ),
    // Calculated fields added by use-invoice-form hook
    netAmount: z.number().optional(),
    netAmountFieldIsVisible: z.boolean().optional(),
    vatAmount: z.number().optional(),
    vatAmountFieldIsVisible: z.boolean().optional(),
    paymentMethod: sanitizedString(z.string().max(100).trim())
      .optional()
      .default(""),
    paymentMethodFieldIsVisible: visible(),
    paymentDate: sanitizedString(z.string().max(50).trim())
      .optional()
      .default(""),
    paymentDateFieldIsVisible: visible(false),
    paymentPhoneNumber: sanitizedString(z.string().max(20).trim())
      .optional()
      .default(""),
    paymentPhoneNumberFieldIsVisible: visible(),
    paymentBankName: sanitizedString(z.string().max(100).trim())
      .optional()
      .default(""),
    paymentBankNameFieldIsVisible: visible(false),
    paymentBankNumber: sanitizedString(z.string().max(10).trim())
      .optional()
      .default(""),
    paymentBankNumberFieldIsVisible: visible(false),
    paymentBranch: sanitizedString(z.string().max(10).trim())
      .optional()
      .default(""),
    paymentBranchFieldIsVisible: visible(false),
    paymentAccount: sanitizedString(z.string().max(20).trim())
      .optional()
      .default(""),
    paymentAccountFieldIsVisible: visible(false),
    // Credit card fields - visible by default when populated
    paymentCardCompany: sanitizedString(z.string().max(100).trim())
      .optional()
      .default(""),
    paymentCardCompanyFieldIsVisible: visible(),
    paymentCardType: sanitizedString(z.string().max(50).trim())
      .optional()
      .default(""),
    paymentCardTypeFieldIsVisible: visible(),
    paymentTransactionNumber: sanitizedString(z.string().max(50).trim())
      .optional()
      .default(""),
    paymentTransactionNumberFieldIsVisible: visible(),
    paymentNumberOfPayments: sanitizedString(z.string().max(10).trim())
      .optional()
      .default(""),
    paymentNumberOfPaymentsFieldIsVisible: visible(),
    paymentCardLastFour: sanitizedString(
      z
        .string()
        .max(4)
        .trim()
        .refine(
          (val: string) => {
            if (!val || val === "") return true;
            return /^\d{4}$/.test(val);
          },
          { message: "יש להזין 4 ספרות בדיוק" },
        ),
    )
      .optional()
      .default(""),
    paymentCardLastFourFieldIsVisible: visible(),
    // Check fields - visible by default when populated
    paymentCheckNumber: sanitizedString(z.string().max(50).trim())
      .optional()
      .default(""),
    paymentCheckNumberFieldIsVisible: visible(),
    paymentCheckBankName: sanitizedString(z.string().max(100).trim())
      .optional()
      .default(""),
    paymentCheckBankNameFieldIsVisible: visible(),
    paymentCheckBranch: sanitizedString(z.string().max(10).trim())
      .optional()
      .default(""),
    paymentCheckBranchFieldIsVisible: visible(),
    paymentCheckAccount: sanitizedString(z.string().max(20).trim())
      .optional()
      .default(""),
    paymentCheckAccountFieldIsVisible: visible(),
    paymentCheckMaturityDate: sanitizedString(z.string().max(50).trim())
      .optional()
      .default(""),
    paymentCheckMaturityDateFieldIsVisible: visible(),
  })
  .strict();

export type InvoiceItemData = z.infer<typeof invoiceItemSchema>;

// Seller Schema - only fields used in PDF
export const sellerSchema = z
  .object({
    name: sanitizedString(
      z.string().min(1, "שם המוכר הוא שדה חובה").max(500).trim(),
    ),
    address: sanitizedString(
      z.string().min(1, "כתובת המוכר היא שדה חובה").max(500).trim(),
    ),
    addressSelectedFromList: z.boolean().optional().default(false),
    phone: sanitizedString(
      z
        .string()
        .max(20)
        .trim()
        .refine(
          (val: string) => {
            if (!val || val === "") return true;
            const israeliPhoneRegex =
              /^(\+972[-\s]?|0)([234589]|5[0-9])[-\s]?\d{3}[-\s]?\d{4}$/;
            return israeliPhoneRegex.test(val.replace(/\s/g, ""));
          },
          {
            message:
              "מספר טלפון לא חוקי. פורמט מקובל: 05X-XXX-XXXX או +972-XX-XXX-XXXX",
          },
        ),
    ).optional(),
    email: sanitizedString(
      z.string().email("כתובת אימייל לא חוקית").max(255).trim(),
    ).optional(),
    emailFieldIsVisible: z.boolean().default(false),
    vatNo: sanitizedString(
      z
        .string()
        .max(20)
        .trim()
        .refine(
          (val: string) => {
            if (!val || val === "") return true;
            const cleanId = val.replace(/[-\s]/g, "");
            if (/^\d{9}$/.test(cleanId)) {
              return validateIsraeliId(cleanId);
            }
            return true;
          },
          { message: "מספר תז לא חוקי. יש לבדוק את ספרת הביקורת" },
        ),
    ).optional(),
  })
  .strict();

export type SellerData = z.infer<typeof sellerSchema>;

// Buyer Schema - only fields used in PDF
export const buyerSchema = z
  .object({
    name: sanitizedString(
      z.string().min(1, "שם הקונה הוא שדה חובה").max(500).trim(),
    ),
    vatNo: sanitizedString(
      z
        .string()
        .max(200)
        .trim()
        .refine(
          (val) => {
            if (!val || val === "") return true;
            const cleanId = val.replace(/[-\s]/g, "");
            if (/^\d{9}$/.test(cleanId)) {
              return validateIsraeliId(cleanId);
            }
            return true;
          },
          { message: "מספר תז לא חוקי. יש לבדוק את ספרת הביקורת" },
        ),
    ).optional(),
    email: sanitizedString(
      z.string().email("כתובת אימייל לא חוקית").max(255).trim(),
    ).optional(),
  })
  .strict();

export type BuyerData = z.infer<typeof buyerSchema>;

// Receipt Number Schema
export const receiptNumberSchema = z.object({
  series: sanitizedString(z.string().max(50).trim())
    .optional()
    .or(z.literal("")),
  number: sanitizedString(
    z
      .string()
      .min(1, "מספר קבלה הוא שדה חובה")
      .max(100)
      .trim()
      .refine((val: string) => validateSequentialReceiptNumber(val).isValid, {
        message: "מספר קבלה לא חוקי או אינו רציף",
      }),
  ),
});

export type ReceiptNumberData = z.infer<typeof receiptNumberSchema>;

// Invoice Schema - only fields used in PDF
export const invoiceSchema = z.object({
  dateFormat: z.enum(SUPPORTED_DATE_FORMATS).default(DEFAULT_DATE_FORMAT),
  currency: z.enum(SUPPORTED_CURRENCIES).default("ILS"),
  logo: sanitizedString(
    z
      .string()
      .trim()
      .refine((val) => {
        if (!val) return true;
        // Check format
        if (!/^data:image\/(jpeg|jpg|png|webp);base64,/.test(val)) return false;
        // Check size (2MB max ≈ 2,796,203 chars for base64)
        if (val.length > 2_796_203) return false;
        // Validate base64 characters after prefix
        const base64Part = val.split(",")[1];
        if (!base64Part) return false;
        // Base64 regex: A-Z, a-z, 0-9, +, /, and = padding
        return /^[A-Za-z0-9+/]*={0,2}$/.test(base64Part);
      }, "הלוגו חייב להיות תמונה תקינה (JPEG, PNG או WebP) בפורמט base64, עד 2MB"),
  )
    .optional()
    .or(z.literal("")),
  receiptNumber: receiptNumberSchema.optional(),
  dateOfIssue: sanitizedString(
    z
      .string()
      .trim()
      // Accept any string - validation happens in transform
      .transform((val: string) => {
        if (!val) return dayjs().format("DD/MM/YYYY");

        // Normalize to DD/MM/YYYY format
        const formats = [
          "DD/MM/YYYY",
          "YYYY-MM-DD",
          "DD-MM-YYYY",
          "YYYY/MM/DD",
          "DD.MM.YYYY",
        ];
        for (const format of formats) {
          const parsed = dayjs(val, format, true);
          if (parsed.isValid()) {
            return parsed.format("DD/MM/YYYY");
          }
        }

        // Fallback to lenient parsing for legacy data
        const parsed = dayjs(val);
        if (parsed.isValid() && parsed.year() > 2000 && parsed.year() < 2100) {
          return parsed.format("DD/MM/YYYY");
        }

        // If all parsing fails, return today's date
        return dayjs().format("DD/MM/YYYY");
      }),
  ),
  seller: sellerSchema,
  buyer: buyerSchema,
  items: z
    .array(invoiceItemSchema)
    .min(1, "פריט אחד לפחות")
    .max(100, "לא ניתן להוסיף יותר מ-100 פריטים"),
  total: z
    .union([z.string(), z.number()])
    .transform((val) => {
      if (typeof val === "string") {
        const trimmed = val.trim();
        if (trimmed === "") throw new Error("הסכום הכולל הוא שדה חובה");
        const num = Number(trimmed);
        if (isNaN(num)) throw new Error("הסכום הכולל חייב להיות מספר");
        return num;
      }
      return val;
    })
    .refine((val) => !isNaN(val) && isFinite(val), {
      message: "הסכום הכולל חייב להיות מספר תקין",
    })
    .refine((val) => val >= 0, { message: "הסכום הכולל חייב להיות לא-שלילי" }),
  paymentMethod: sanitizedString(z.string().max(500).trim()).optional(),
  paymentMethodFieldIsVisible: z.boolean().default(true),
  notes: sanitizedString(z.string().max(750).trim()).optional(),
  notesFieldIsVisible: z.boolean().default(true),
  businessType: z.enum(BUSINESS_TYPES).optional().default("osek-patur"),
});

export type InvoiceData = z.infer<typeof invoiceSchema>;
export type ReceiptData = InvoiceData & {
  _type?: "receipt";
  displayReceiptNumber?: string;
};

// Local Storage Keys - using secure naming convention
export const PDF_DATA_LOCAL_STORAGE_KEY = STORAGE_KEYS.PDF_FORM_DATA;

export const accordionSchema = z
  .object({
    general: z.boolean(),
    seller: z.boolean(),
    buyer: z.boolean(),
    invoiceItems: z.boolean(),
  })
  .strict();

export type AccordionState = z.infer<typeof accordionSchema>;

export const ACCORDION_STATE_LOCAL_STORAGE_KEY = STORAGE_KEYS.ACCORDION_STATE;

export const MOBILE_TABS_VALUES = ["invoice-form", "invoice-preview"] as const;
export const DEFAULT_MOBILE_TAB = MOBILE_TABS_VALUES[0];
export type MobileTabsValues = (typeof MOBILE_TABS_VALUES)[number];

export const metadataSchema = z.object({
  appVersion: z.string().default(APP_VERSION),
  schemaVersion: z.string().default(SCHEMA_VERSION),
  invoiceCreatedAt: z
    .string()
    .datetime()
    .default(() => dayjs().toISOString()),
  invoiceLastUpdatedAt: z.string().datetime().optional(),
  lastVisitedMobileTab: z
    .enum(MOBILE_TABS_VALUES)
    .default(DEFAULT_MOBILE_TAB)
    .optional(),
  invoiceDownloadCount: z.number().int().nonnegative().default(0),
});

export type Metadata = z.infer<typeof metadataSchema>;
export const METADATA_LOCAL_STORAGE_KEY = STORAGE_KEYS.METADATA;

// Saved Receipts Schema
export const savedReceiptSchema = z.object({
  id: z.string(),
  invoiceData: invoiceSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  displayName: z.string().optional(),
});

export type SavedReceipt = z.infer<typeof savedReceiptSchema>;

export const savedReceiptsCollectionSchema = z.object({
  receipts: z.array(savedReceiptSchema),
  version: z.string().default("1.0"),
});

export type SavedReceiptsCollection = z.infer<
  typeof savedReceiptsCollectionSchema
>;
export const SAVED_RECEIPTS_LOCAL_STORAGE_KEY = STORAGE_KEYS.RECEIPTS_DATA;
